import React, { useEffect, useState } from "react";
import { Box, Flex, Grid } from "theme-ui";
import { useNavigate } from "react-router-dom";
import * as DashboardAPI from "api/Dashboard";
import ProjectCard from "./ProjectCard";
import P from "components/typography/P";
import BasicButton from "components/forms/BasicButton";
import { themevals } from "theme/themevals";

interface ClientProject {
  id: string;
  projectNumber: string;
  projectName: string;
  status: string;
  statusColor: string;
  serviceDate: string | null;
  locationAddress: string;
}

const MyClientProjects: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ClientProject[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const data = await DashboardAPI.getClientProjects(6);
        if (data) {
          setProjects(data.projects);
          setTotalCount(data.totalCount);
        }
        setError(null);
      } catch (err) {
        console.error("Error fetching client projects:", err);
        setError("Failed to load projects");
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const handleViewAll = () => {
    navigate("/projects");
  };

  if (loading) {
    return (
      <Box sx={{ padding: "24px" }}>
        <P fontFamily="Poppins" fontSize="14px" color="#6B7280">
          Loading projects...
        </P>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: "24px" }}>
        <P fontFamily="Poppins" fontSize="14px" color={themevals.colors.danger}>
          {error}
        </P>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "white",
        border: "1px solid",
        borderColor: themevals.colors.inputOutline,
        borderRadius: "8px",
        boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
      }}
    >
      {/* Section Header */}
      <Flex
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          borderBottom: "1px solid",
          borderColor: themevals.colors.inputOutline,
          minHeight: "76px",
        }}
      >
        <P
          fontFamily="Poppins"
          fontSize="14px"
          fontWeight="600"
          color="black"
          marginTop="0"
          marginBottom="0"
        >
          My Projects
        </P>
        {totalCount > 6 && (
          <BasicButton
            scheme="secondary"
            outline={true}
            onClick={handleViewAll}
          >
            View All
          </BasicButton>
        )}
      </Flex>

      {/* Projects Grid */}
      <Box sx={{ padding: "24px" }}>
        {projects.length === 0 ? (
          <Box
            sx={{
              backgroundColor: "#F9FAFB",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              padding: "32px",
              textAlign: "center",
            }}
          >
            <P fontFamily="Poppins" fontSize="14px" color="#6B7280" marginTop="0" marginBottom="0">
              No active projects
            </P>
          </Box>
        ) : (
          <Grid
          columns={3}
          gap="16px"
            sx={{
            "@media screen and (max-width: 1024px)": {
              gridTemplateColumns: "repeat(2, 1fr)",
            },
            "@media screen and (max-width: 640px)": {
              gridTemplateColumns: "1fr",
            },
          }}
        >
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                id={project.id}
                projectNumber={project.projectNumber}
                projectName={project.projectName}
                status={project.status}
                statusColor={project.statusColor}
                serviceDate={project.serviceDate}
                locationAddress={project.locationAddress}
              />
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default MyClientProjects;
